import {
  Box,
  BoxProps,
  Button,
  Divider,
  TextField,
  Typography
} from '@material-ui/core';
import debounce from 'lodash.debounce';
import startCase from 'lodash.startcase';
import React, { useRef, useState } from 'react';

import { LegislativeDistrict } from '../../../../services/APIService';
import CumulativeSelection from '../CumulativeSelection';
import MultipleSelection from '../MultipleSelection';
import RangeSelection, { Histogram, HistogramValue } from '../RangeSelection';
import SingleSelection, {
  OptionValue as SingleOptionValue
} from '../SingleSelection';

const AWBD_REGIONS = ['Central', 'Gulf Coast', 'North', 'West'];

type HistogramFilter = {
  histogram: Histogram;
  defaultValue: HistogramValue | undefined;
};

type MultipleOptionFilter<T> = {
  options: T[];
  defaultValue: string[] | undefined;
};

enum ToggleFilter {
  Region = 'region',
  DistrictType = 'districtType',
  LegislativeDistrict = 'legislativeDistrict',
  Acreage = 'acreage',
  YearCreated = 'yearCreated',
  AssessedValue = 'assessedValue',
  WaterConnections = 'waterConnections',
  SanitaryConnections = 'sanitaryConnections',
  BondsOutstanding = 'bondsOutstanding'
}

type FilterValue = {
  acreage: HistogramValue;
  yearCreated: HistogramValue;
  bondsOutstanding: HistogramValue;
  assessedValue: HistogramValue;
  waterConnections: HistogramValue;
  sanitaryConnections: HistogramValue;
  districtName: string;
  awbdMembership: SingleOptionValue;
  region: string[];
  districtType: string[];
};

export interface FilterPanelProps {
  filterConfig: {
    districtName: { defaultValue: string | undefined };
    awbdMembership: { defaultValue: SingleOptionValue };
    region: { defaultValue: string[] | undefined };
    districtType: MultipleOptionFilter<string>;
    legislativeDistrict: MultipleOptionFilter<LegislativeDistrict>;
    acreage: HistogramFilter;
    yearCreated: HistogramFilter;
    bondsOutstanding: HistogramFilter;
    assessedValue: HistogramFilter;
    waterConnections: HistogramFilter;
    sanitaryConnections: HistogramFilter;
  };
  filterResultsCount: number;
  onClose: () => void;
  onFilterChange: ({
    acreage,
    yearCreated,
    bondsOutstanding,
    assessedValue,
    waterConnections,
    sanitaryConnections,
    awbdMembership,
    region,
    districtType
  }: FilterValue) => void;
}

const FilterPanel: React.VFC<FilterPanelProps & BoxProps> = ({
  filterConfig,
  filterResultsCount,
  onClose,
  onFilterChange,
  ...rootProps
}) => {
  const defaultValue = useRef({
    acreage: filterConfig.acreage.defaultValue,
    yearCreated: filterConfig.yearCreated.defaultValue,
    bondsOutstanding: filterConfig.bondsOutstanding.defaultValue,
    assessedValue: filterConfig.assessedValue.defaultValue,
    waterConnections: filterConfig.waterConnections.defaultValue,
    sanitaryConnections: filterConfig.sanitaryConnections.defaultValue,
    districtName: filterConfig.districtName.defaultValue,
    awbdMembership: filterConfig.awbdMembership.defaultValue,
    region: filterConfig.region.defaultValue,
    districtType: filterConfig.districtType.defaultValue,
    legislativeDistrict: filterConfig.legislativeDistrict.defaultValue
  });

  const [resetStatus, setResetStatus] = useState(false);
  const [filterValue, setFilterValue] = useState(defaultValue.current);
  const [filterToggleStatus, setFilterToggleStatus] = useState(() =>
    Object.values(ToggleFilter).reduce((acc, filter) => {
      acc[filter] = !!filterConfig[filter].defaultValue;
      return acc;
    }, {} as Record<ToggleFilter, boolean>)
  );

  const handleSubmitFilter = (
    filter: typeof filterValue,
    toggleStatus: Record<ToggleFilter, boolean>
  ) => {
    const value = Object.keys(filter).reduce((acc, key) => {
      let fieldValue = filter[key as keyof typeof filterValue];

      if (['districtName', 'awbdMembership'].includes(key)) {
        acc[key as keyof typeof filterValue] = fieldValue;
        return acc;
      }

      if (!toggleStatus[key as ToggleFilter]) {
        fieldValue = undefined;
      } else if (fieldValue === undefined) {
        if (
          [
            ToggleFilter.Region,
            ToggleFilter.DistrictType,
            ToggleFilter.LegislativeDistrict
          ].includes(key as ToggleFilter)
        ) {
          fieldValue = [];
        } else {
          const { min, max } = (
            filterConfig[key as keyof typeof filterConfig] as HistogramFilter
          ).histogram;

          fieldValue = { min, max };
        }
      }

      acc[key as keyof typeof filterValue] = fieldValue;
      return acc;
    }, {} as Record<keyof typeof filterValue, HistogramValue | SingleOptionValue | string[] | string>);

    onFilterChange(value as FilterValue);
  };

  const handleReset = () => {
    const filterToggleStatus = Object.values(ToggleFilter).reduce(
      (acc, filter) => {
        acc[filter] = false;
        return acc;
      },
      {} as Record<ToggleFilter, boolean>
    );

    const filterValue = {
      ...defaultValue.current,
      districtName: undefined,
      awbdMembership: undefined,
      region: undefined
    };

    setResetStatus(!resetStatus);
    setFilterValue({ ...filterValue });
    setFilterToggleStatus({ ...filterToggleStatus });
    handleSubmitFilter(filterValue, filterToggleStatus);
  };

  const handleFilterChange = (
    prop: keyof typeof filterValue,
    value: HistogramValue | SingleOptionValue | string[] | string
  ) => {
    const filter = { ...filterValue };

    filter[prop] = value as never;
    setFilterValue({ ...filter });
    handleSubmitFilter(filter, filterToggleStatus);
  };

  const handleToggleChange = (prop: ToggleFilter, value: boolean) => {
    filterToggleStatus[prop] = value;
    setFilterToggleStatus({ ...filterToggleStatus });
    handleSubmitFilter(filterValue, filterToggleStatus);
  };

  const {
    districtName,
    awbdMembership,
    region,
    districtType,
    legislativeDistrict,
    ...histogramFilters
  } = filterConfig;

  const debouncedNameChange = debounce((districtName) => {
    handleFilterChange('districtName', districtName);
  }, 500);

  return (
    <Box
      {...rootProps}
      key={resetStatus ? 'reset' : 'freeze'}
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Box
        px={2}
        py={2}
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems="flex-start"
      >
        <React.Fragment>
          <Box mx={1} flex="1 1 auto">
            <Typography variant="h4">
              {filterResultsCount.toLocaleString()} District
              {filterResultsCount !== 1 && 's'}
            </Typography>
          </Box>

          <Box
            flex="0 0 auto"
            mt={{ xs: 1, sm: 0 }}
            display="flex"
            flexDirection="row"
          >
            <Button variant="text" color="primary" onClick={handleReset}>
              Reset Filters
            </Button>
            <Box ml={1}>
              <Button variant="text" color="primary" onClick={onClose}>
                Results
              </Button>
            </Box>
          </Box>
        </React.Fragment>
      </Box>

      <Divider />

      <Box py={2} px={3} flex={1} overflow="hidden auto">
        <Box>
          <Typography variant="h4">District Name</Typography>

          <Box mt={2}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Any part of the district name"
              defaultValue={districtName.defaultValue}
              onChange={(evt) => debouncedNameChange(evt.target.value)}
            />
          </Box>
        </Box>

        <Box mt={3}>
          <SingleSelection
            title="AWBD Membership"
            options={[
              { label: 'AWBD Members', value: true },
              { label: 'Non-Members', value: false },
              { label: 'All Districts', value: undefined }
            ]}
            defaultValue={awbdMembership.defaultValue}
            onChange={(value) => handleFilterChange('awbdMembership', value)}
          />
        </Box>

        <Box mt={3}>
          <MultipleSelection
            title="AWBD Region"
            toggleOn={filterToggleStatus.region}
            options={AWBD_REGIONS.map((region) => ({
              label: region,
              value: region
            }))}
            defaultValue={region.defaultValue}
            onChange={(value) => handleFilterChange('region', value)}
            onToggleChange={(value) => {
              handleToggleChange(ToggleFilter.Region, value);
            }}
          />
        </Box>

        <Box mt={3}>
          <MultipleSelection
            title="District Type"
            toggleOn={filterToggleStatus.districtType}
            options={districtType.options.map((option) => ({
              label: option,
              value: option
            }))}
            defaultValue={districtType.defaultValue}
            onChange={(value) => handleFilterChange('districtType', value)}
            onToggleChange={(value) => {
              handleToggleChange(ToggleFilter.DistrictType, value);
            }}
          />
        </Box>

        <Box mt={3}>
          <CumulativeSelection
            title="Legislative District"
            placeholder="Legislative district name or representative"
            toggleOn={filterToggleStatus.legislativeDistrict}
            options={legislativeDistrict.options}
            defaultValue={legislativeDistrict.options.filter((option) =>
              legislativeDistrict.defaultValue?.includes(option.districtId)
            )}
            getOptionLabel={(option) =>
              `${option.districtName} (${option.electedOfficial})`
            }
            onChange={(value) =>
              handleFilterChange(
                'legislativeDistrict',
                value.map((option) => option.districtId)
              )
            }
            onToggleChange={(value) => {
              handleToggleChange(ToggleFilter.LegislativeDistrict, value);
            }}
          />
        </Box>

        {Object.keys(histogramFilters).map((key) => (
          <Box key={key} mt={4}>
            <RangeSelection
              title={startCase(key)}
              displayOrigin={key === 'yearCreated'}
              toggleOn={filterToggleStatus[key as ToggleFilter]}
              histogram={
                histogramFilters[key as keyof typeof histogramFilters].histogram
              }
              defaultValue={
                histogramFilters[key as keyof typeof histogramFilters]
                  .defaultValue
              }
              onChange={(value) =>
                handleFilterChange(key as keyof typeof histogramFilters, value)
              }
              onToggleChange={(value) =>
                handleToggleChange(key as ToggleFilter, value)
              }
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FilterPanel;
